import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AuthenticationService } from '../../../../_services';
import { Company, User } from '../../../../_models';
import { GRANTS_BUREAU_ID, GRANTS_BUREAU_NAME } from 'app/app.constants';

export interface BureauSelection {
  companyId: string;
  companyName: string;
  bureauId: string;
  bureauName: string;
}

export interface GrantsSelection {
  companyId: string;
  companyName: string;
}

export interface DefaultBureauSelection {
  companyId: string;
  bureauId: string;
}

@Component({
  selector: 'bk-context-switcher',
  templateUrl: './bk-context-switcher.component.html',
  styleUrls: ['./bk-context-switcher.component.scss'],
})
export class BkContextSwitcherComponent {
  @Input() companies: Company[] = [];
  @Input() label: string | null = null;
  @Input() image: string | null = null;
  @Input() canSwitch = true;
  @Input() activeCompanyId: string | null = null;
  @Input() activeBureauId: string | null = null;
  @Input() user: User | null = null;

  @Output() bureauSelect = new EventEmitter<BureauSelection>();
  @Output() grantsSelect = new EventEmitter<GrantsSelection>();
  @Output() defaultBureauChange = new EventEmitter<DefaultBureauSelection>();

  GRANTS_BUREAU_ID = GRANTS_BUREAU_ID;
  GRANTS_BUREAU_NAME = GRANTS_BUREAU_NAME;

  mobileOpen = false;

  constructor(public authenticationService: AuthenticationService) {}

  /** Used by the site header when the route changes so a stuck overlay can't block taps. */
  closeMobile(): void {
    this.mobileOpen = false;
  }

  get displayLabel(): string {
    return this.label && this.label.trim() ? this.label : 'Select organization';
  }

  get showChevron(): boolean {
    return this.canSwitch;
  }

  choose(companyId: string, companyName: string, bureauId: string, bureauName: string): void {
    this.mobileOpen = false;
    this.bureauSelect.emit({ companyId, companyName, bureauId, bureauName });
  }

  chooseGrants(company: Company): void {
    this.mobileOpen = false;
    this.grantsSelect.emit({ companyId: company.id, companyName: company.name });
  }

  setDefault(companyId: string, bureauId: string, event: Event): void {
    event.stopPropagation();
    this.defaultBureauChange.emit({ companyId, bureauId });
  }

  companyHasBureauAccess(company: any): boolean {
    return company?.access_bureau !== false;
  }

  companyHasGrantsAccess(company: any): boolean {
    return !!(company && company.access_grants_sponsorships);
  }

  companyScopeCount(company: Company): number {
    let count = 0;
    if (this.companyHasBureauAccess(company)) {
      count += (company.bureaus || []).length;
    }
    if (this.companyHasGrantsAccess(company)) {
      count += 1;
    }
    return count;
  }

  showCompanySubmenu(company: Company): boolean {
    return this.authenticationService.is_staff() || this.companyScopeCount(company) > 0;
  }

  directSelectCompanyScope(company: Company): void {
    if (this.companyHasBureauAccess(company) && company.bureaus?.length) {
      const [bureau] = company.bureaus;
      this.choose(company.id, company.name, bureau.id, bureau.name);
      return;
    }
    if (this.companyHasGrantsAccess(company)) {
      this.chooseGrants(company);
    }
  }

  visibleBureaus(company: Company) {
    if (!this.companyHasBureauAccess(company)) {
      return [];
    }
    return company.bureaus || [];
  }

  isDefault(companyId: string, bureauId: string): boolean {
    return !!this.user && companyId === this.user.default_company_id && bureauId === this.user.default_bureau_id;
  }

  /** The bureau the user is currently viewing (highlighted in the mobile list). */
  isActive(companyId: string, bureauId: string): boolean {
    return !!companyId && companyId === this.activeCompanyId && bureauId === this.activeBureauId;
  }

  /** The company that contains the active bureau (highlights the parent row). */
  isActiveCompany(companyId: string): boolean {
    return !!companyId && companyId === this.activeCompanyId;
  }
}
